import { LiveAnnouncer } from '@angular/cdk/a11y';
import { Service, inject, signal } from '@angular/core';
import {
    type NotificationItem,
    type NotificationOptions,
    type NotificationSeverity,
} from './notification.model';

/** Сколько карточек видно одновременно; остальные ждут в очереди. */
const MAX_VISIBLE = 5;
const DEFAULT_DURATION = 3_000;
/** Ошибку пользователь закрывает сам — она не должна исчезать из-под рук. */
const ERROR_DURATION = 0;

/**
 * Стопка уведомлений в стиле Material snackbar.
 *
 * MatSnackBar держит только один snackbar за раз (новый закрывает предыдущий), поэтому
 * стек, очередь и таймеры живут здесь, а рисует их `NotificationStack`.
 */
@Service()
export class NotificationService {
    private readonly liveAnnouncer = inject(LiveAnnouncer);

    private readonly visibleItems = signal<readonly NotificationItem[]>([]);
    private readonly queuedItems = signal<readonly NotificationItem[]>([]);
    private lastId = 0;

    /** Карточки, которые сейчас на экране. */
    readonly visible = this.visibleItems.asReadonly();

    /** Показывает уведомление; возвращает его id. */
    show(message: string, options: NotificationOptions = {}): number {
        const severity = options.severity ?? 'info';

        if (options.dedupe !== false) {
            const duplicate = this.findDuplicate(message, severity);
            if (duplicate) {
                this.extend(duplicate);
                return duplicate.id;
            }
        }

        const item: NotificationItem = {
            id: ++this.lastId,
            message,
            severity,
            duration: options.duration ?? defaultDuration(severity),
            action: options.action,
            revision: 0,
        };

        if (this.visibleItems().length < MAX_VISIBLE) {
            this.visibleItems.update((items) => [...items, item]);
        } else {
            this.queuedItems.update((items) => [...items, item]);
        }

        this.announce(item);
        return item.id;
    }

    success(message: string, options: NotificationOptions = {}): number {
        return this.show(message, { ...options, severity: 'success' });
    }

    info(message: string, options: NotificationOptions = {}): number {
        return this.show(message, { ...options, severity: 'info' });
    }

    warning(message: string, options: NotificationOptions = {}): number {
        return this.show(message, { ...options, severity: 'warning' });
    }

    error(message: string, options: NotificationOptions = {}): number {
        return this.show(message, { ...options, severity: 'error' });
    }

    /** Закрывает уведомление; освободившееся место занимает следующее из очереди. */
    dismiss(id: number): void {
        const visible = this.visibleItems();
        if (!visible.some((item) => item.id === id)) {
            this.queuedItems.update((items) => items.filter((item) => item.id !== id));
            return;
        }

        this.visibleItems.set(visible.filter((item) => item.id !== id));
        this.promote();
    }

    /** Закрывает всё, включая очередь. */
    clear(): void {
        this.visibleItems.set([]);
        this.queuedItems.set([]);
    }

    /** Повтор того же текста не плодит карточку, а перезапускает её таймер. */
    private findDuplicate(
        message: string,
        severity: NotificationSeverity,
    ): NotificationItem | undefined {
        return [...this.visibleItems(), ...this.queuedItems()].find(
            (item) => item.message === message && item.severity === severity,
        );
    }

    private extend(duplicate: NotificationItem): void {
        const bump = (items: readonly NotificationItem[]): readonly NotificationItem[] =>
            items.map((item) =>
                item.id === duplicate.id ? { ...item, revision: item.revision + 1 } : item,
            );

        this.visibleItems.update(bump);
        this.queuedItems.update(bump);
    }

    private promote(): void {
        const free = MAX_VISIBLE - this.visibleItems().length;
        const queued = this.queuedItems();
        if (free <= 0 || queued.length === 0) return;

        this.visibleItems.update((items) => [...items, ...queued.slice(0, free)]);
        this.queuedItems.set(queued.slice(free));
    }

    private announce(item: NotificationItem): void {
        const politeness = item.severity === 'error' ? 'assertive' : 'polite';
        void this.liveAnnouncer.announce(item.message, politeness);
    }
}

function defaultDuration(severity: NotificationSeverity): number {
    return severity === 'error' ? ERROR_DURATION : DEFAULT_DURATION;
}
